import Papa from 'papaparse';
import {
  EnrichmentComponent,
  EnrichmentStatus,
} from '../enums/csv-enrichment.enums';
import { ColumnMapping, UploadedFileInfo } from '../types/csv-enrichment.types';

const REVEAL_LABELS: Record<string, string> = {
  [EnrichmentComponent.EMAIL]: 'Email',
  [EnrichmentComponent.PHONE_EMAIL]: 'Email + Phone',
  'email + phone': 'Email + Phone',
  'email+phone': 'Email + Phone',
  'email phone': 'Email + Phone',
};

export const getComponentDisplayLabel = (
  value: EnrichmentComponent | string | null | undefined,
): string => {
  if (value == null || value === '') return '';
  const key = String(value).toLowerCase().replace(/\s+/g, ' ').trim();
  return REVEAL_LABELS[key] ?? String(value);
};

export const getStatusDisplayLabel = (status: EnrichmentStatus): string => {
  switch (status) {
    case EnrichmentStatus.PROCESSING:
      return null;
    case EnrichmentStatus.QUEUED:
      return 'Queued';
    case EnrichmentStatus.COMPLETED:
      return 'Completed';
    case EnrichmentStatus.FAILED:
      return 'Failed';
    case EnrichmentStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};

export const getStatusColorClass = (status: EnrichmentStatus): string => {
  switch (status) {
    case EnrichmentStatus.COMPLETED:
      return 'bs-csv-enrichment__completed-txt';
    case EnrichmentStatus.PROCESSING:
      return 'blue-txt-6';
    case EnrichmentStatus.QUEUED:
      return 'gold-txt-6';
    case EnrichmentStatus.FAILED:
      return 'red-txt-18';
    case EnrichmentStatus.CANCELLED:
      return 'gray-txt-11';
    default:
      return 'gray-txt-15';
  }
};

// Module-level file reference — set during CSV parsing, used during job creation.
// Avoids storing non-serializable File in Redux state.
let pendingCsvFile: File | null = null;

export const setPendingCsvFile = (file: File | null): void => {
  pendingCsvFile = file;
};

export const getPendingCsvFile = (): File | null => pendingCsvFile;

const FIRST_NAME_PATTERNS = [
  'first name',
  'first_name',
  'firstname',
  'fname',
  'given name',
  'given_name',
  'f_name',
  'first',
  'given',
  'forename',
  'name',
];

const LAST_NAME_PATTERNS = [
  'last name',
  'last_name',
  'lastname',
  'lname',
  'surname',
  'family name',
  'family_name',
  'l_name',
  'last',
  'family',
];

const COMPANY_NAME_PATTERNS = [
  'company',
  'company name',
  'company_name',
  'companyname',
  'organization',
  'org',
  'organisation',
];

const COMPANY_DOMAIN_PATTERNS = [
  'domain',
  'company domain',
  'company_domain',
  'website',
  'url',
];

const LINKEDIN_URL_PATTERNS = [
  'linkedin',
  'linkedin url',
  'linkedin_url',
  'linkedin profile',
];

const matchHeader = (header: string, patterns: string[]): boolean => {
  const normalized = header.trim().toLowerCase();
  return patterns.some((p) => normalized === p);
};

export const autoDetectColumnMapping = (headers: string[]): ColumnMapping => {
  const mapping: ColumnMapping = {
    firstName: null,
    lastName: null,
    companyName: null,
    companyDomain: null,
    linkedinUrl: null,
  };

  headers.forEach((header) => {
    if (!mapping.firstName && matchHeader(header, FIRST_NAME_PATTERNS)) {
      mapping.firstName = header;
    }
    if (!mapping.lastName && matchHeader(header, LAST_NAME_PATTERNS)) {
      mapping.lastName = header;
    }
    if (!mapping.companyName && matchHeader(header, COMPANY_NAME_PATTERNS)) {
      mapping.companyName = header;
    }
    if (
      !mapping.companyDomain &&
      matchHeader(header, COMPANY_DOMAIN_PATTERNS)
    ) {
      mapping.companyDomain = header;
    }
    if (!mapping.linkedinUrl && matchHeader(header, LINKEDIN_URL_PATTERNS)) {
      mapping.linkedinUrl = header;
    }
  });

  return mapping;
};

export const parseCSVHeaders = (file: File): Promise<UploadedFileInfo> => {
  pendingCsvFile = file;
  return new Promise((resolve, reject) => {
    let rowCount = 0;
    let headers: string[] = [];
    const sampleRow: Record<string, string> = {};
    Papa.parse(file, {
      skipEmptyLines: 'greedy',
      step: (row) => {
        if (rowCount === 0) {
          headers = row.data as string[];
        } else if (rowCount === 1) {
          const data = row.data as string[];
          headers.forEach((header, i) => {
            sampleRow[header] = data[i] || '';
          });
        }
        rowCount += 1;
      },
      complete: () => {
        resolve({
          name: file.name,
          headers,
          rowCount: Math.max(rowCount - 1, 0), // exclude header row
          sampleRow,
        });
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

export const COLUMN_TO_API_SORT_KEY: Record<string, string> = {
  fileName: 'fileName',
  createdAt: 'date',
  totalRecords: 'totalRecords',
  enrichedRecords: 'enrichedRecords',
};

export const API_TO_COLUMN_SORT_KEY: Record<string, string> = {
  fileName: 'fileName',
  date: 'createdAt',
  totalRecords: 'totalRecords',
  enrichedRecords: 'enrichedRecords',
};

export const getEnrichmentApiSuccessMessage = (
  response: unknown,
  fallback: string,
): string => {
  const r = response as {
    message?: unknown;
    payload?: { message?: unknown };
  };
  const msg = r?.message ?? r?.payload?.message;
  if (typeof msg === 'string' && msg.trim().length > 0) {
    return msg.trim();
  }
  return fallback;
};
